import type { FC, ReactNode, PropsWithChildren } from 'react';

import {
  linkContainerClass,
  styledMainRecipe,
  styledRootArrowClass,
  styledStackClass,
  styledSVGGroupClass,
  styledTitleContainerClass,
  styledTopClass,
  subHeaderBackgroundClass,
} from './DesktopHeader.css';
import { useI18nTranslations } from '../../providers/i18n';
import { Box } from '../Box/Box';
import { PageGrid } from '../PageGrid/PageGrid';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

type CompoundHeaderProps = FC<{
  children: ReactNode;
}> & {
  Main: typeof Main;
  Top: typeof Top;
  Sub: typeof Sub;
};

export const DesktopHeader: CompoundHeaderProps = ({ children }) => <>{children}</>;

type MainProps = {
  children: ReactNode;
  /** Logo that links back to the homepage, which should have an accessible description. */
  logoLink: ReactNode;
  languageSelector?: ReactNode;
  profileLink?: ReactNode;
  buttonLink?: ReactNode;
  searchComponent?: ReactNode;
  isOpenDomain?: boolean;
};

const Main: FC<PropsWithChildren<MainProps>> = ({
  children,
  logoLink,
  languageSelector,
  profileLink,
  buttonLink,
  searchComponent,
  isOpenDomain,
}) => {
  const { languageAndProfile, profileNavigation } = useI18nTranslations();
  return (
    <div className={styledMainRecipe({ isOpenDomain })}>
      <PageGrid>
        <PageGrid.Item gridColumn={{ initial: '1/-1' }}>
          <Stack className={styledStackClass} direction="row" alignY="center">
            <Stack.Item grow>
              <Stack direction="row" alignY="center">
                {logoLink}
                <Stack.Item grow>
                  <Box paddingX="5">{children}</Box>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack
              as="nav"
              direction="row"
              alignY="center"
              gap="4"
              aria-label={languageSelector ? languageAndProfile : profileNavigation}>
              {languageSelector}
              {profileLink}
              {buttonLink}
              {searchComponent}
            </Stack>
          </Stack>
        </PageGrid.Item>
      </PageGrid>
    </div>
  );
};

type TopProps = {
  children: ReactNode;
  languageSelector?: ReactNode;
};

const Top: FC<PropsWithChildren<TopProps>> = ({ children, languageSelector }) => {
  const { domainNavigation, languageNavigation } = useI18nTranslations();
  return (
    <Box className={styledTopClass} paddingTop="3" paddingBottom="1">
      <PageGrid>
        <PageGrid.Item gridColumn={{ initial: '1/-1' }}>
          <Stack direction="row" gap="5" alignX="end" alignY="center">
            <nav aria-label={domainNavigation}>
              <Stack direction="row" gap="5" alignX="end" alignY="center" as="ul">
                {children}
              </Stack>
            </nav>
            {languageSelector && (
              <nav aria-label={languageNavigation}>
                <Stack direction="row" gap="5" alignX="end" alignY="center">
                  {languageSelector}
                </Stack>
              </nav>
            )}
          </Stack>
        </PageGrid.Item>
      </PageGrid>
    </Box>
  );
};

const RootArrow: FC = () => (
  <div className={styledRootArrowClass}>
    <svg xmlns="http://www.w3.org/2000/svg" width="auto" height="100%" viewBox="0 0 18 60">
      <g className={styledSVGGroupClass} fill="none" fillRule="evenodd">
        <path
          d="M209 0l16.235 23.743c2.28 3.333 2.33 7.71.129 11.096L209 60V0z"
          transform="translate(-209 -100) translate(0 17) translate(0 83)"
        />
      </g>
    </svg>
  </div>
);

type SubProps = {
  children: ReactNode;
  title: ReactNode;
};

const Sub: FC<PropsWithChildren<SubProps>> = ({ children, title }) => {
  const { secondaryNavigation } = useI18nTranslations();

  return (
    <div className={subHeaderBackgroundClass}>
      <PageGrid>
        <PageGrid.Item gridColumn={{ initial: '1/-1' }}>
          <Stack direction="row" alignY="center">
            <Stack className={styledTitleContainerClass} alignY="center">
              <Text weight="bold" size="BodyL" whiteSpace="nowrap">
                {title}
              </Text>
            </Stack>
            <Stack className={linkContainerClass} direction="row" alignY="center">
              <RootArrow />
              <nav aria-label={secondaryNavigation}>
                <Box padding="5" paddingLeft="10">
                  <Stack as="ul" alignY="center" direction="row" gap="5" wrap>
                    {children}
                  </Stack>
                </Box>
              </nav>
            </Stack>
          </Stack>
        </PageGrid.Item>
      </PageGrid>
    </div>
  );
};

DesktopHeader.Top = Top;
DesktopHeader.Main = Main;
DesktopHeader.Sub = Sub;

RootArrow.displayName = 'RootArrow';
Sub.displayName = 'Sub';
DesktopHeader.displayName = 'DeskthopHeader';
DesktopHeader.Top.displayName = 'DesktopHeader.Top';
DesktopHeader.Main.displayName = 'DesktopHeader.Main';
DesktopHeader.Sub.displayName = 'DesktopHeader.Sub';
